import * as THREE from "three";
import { assetUrl } from "../assetVer.js"; // ?v=<build> cache-buster so re-rendered terrain textures refresh on deploy
import { KAPON_TERRAIN as T } from "@ghostland/shared";
import { grassTuftTexture, GRASS_TUFT_VARIANTS } from "./grass.js"; // the cuttable-grass tuft model
import { distanceBlur } from "./distanceBlur.js"; // HD-2D far-blur (per-sprite, by distance to player)
import { dayNight } from "./dayNight.js"; // day/night tint (sprites darken/cool at night)
import { cameraView } from "./cameraView.js"; // current orbit-camera yaw (for directional props)
import type { Blocker } from "./streetScene.js"; // type-only (no runtime cycle with streetScene)

/**
 * Kapon terrain — an Octopath-Traveller-style fantasy meadow: a large, mostly FLAT grassy field with
 * only very subtle undulation toward the edges (NOT the old open-world rolling hills). A paved lane
 * runs a little way out of the city gate, then becomes a dirt trail on the grass; the field is blanketed
 * in grass tufts, dotted with tree groves + bushes, and bounded on the north by a river crossed by a
 * (ruined) stone bridge, with a small lake in the woods. `kaponHeightAt(wx,wz)` is the single ground-
 * height source (world units); it returns 0 outside the Kapon rect, so callers apply it everywhere.
 */

const PX = 32; // server px per world unit (U = 1/32)
const SCENE = "/assets/scene";
/** Local nearest-filter texture loader (avoids importing from streetScene at runtime → no cycle). */
function pixTex(path: string): THREE.Texture {
  const t = new THREE.TextureLoader().load(assetUrl(path));
  t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
function tile(path: string, rx: number, ry: number): THREE.Texture {
  const t = pixTex(path); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(rx, ry); return t;
}

function smooth(a: number, b: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

const PATH_W = 3.4;    // road width (world units) — ~3 tiles, through the open area + forks
const PAVED_LEN = 11;  // how far the paved lane extends out of the city gate before it turns to dirt
const GATE_Z = T.cz + T.hd; // south gate line

/** Distance from a point to the line SEGMENT (ax,az)→(bx,bz). */
function segDist(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const vx = bx - ax, vz = bz - az;
  const wx = px - ax, wz = pz - az;
  const len2 = vx * vx + vz * vz || 1;
  const t = Math.max(0, Math.min(1, (wx * vx + wz * vz) / len2));
  return Math.hypot(px - (ax + t * vx), pz - (az + t * vz));
}

/** Distance to the ROAD NETWORK: the N–S trunk (gate → fork) + the two diagonal forks
 *  (fork → cave east, fork → forest west). 0 on a road, growing off it. */
export function kaponRoadDist(wx: number, wz: number): number {
  return Math.min(
    segDist(wx, wz, T.cx, GATE_Z, T.cx, T.forkZ),        // trunk
    segDist(wx, wz, T.cx, T.forkZ, T.cave.x, T.cave.z),   // east fork → cave
    segDist(wx, wz, T.cx, T.forkZ, T.forest.x, T.forest.z), // west fork → forest
  );
}

/** Ground height at world (wx,wz). 0 outside the Kapon rect (flat everywhere else). Gentle + subtle. */
export function kaponHeightAt(wx: number, wz: number): number {
  if (wx < T.cx - T.hw || wx > T.cx + T.hw || wz < T.cz - T.hd || wz > T.cz + T.hd) return 0;
  const dx = wx - T.cx, dz = wz - T.cz;
  const clear = smooth(T.clearR, T.clearR + T.clearRamp, Math.hypot(dx, dz)); // 0 in the big flat clearing
  const half = wz > T.narrowZ ? T.entryHalf : T.pathHalf;                     // narrow entry corridor → wider road
  const road = smooth(half, half + 3, kaponRoadDist(wx, wz));                 // 0 on the road network
  const lakeD = Math.hypot(wx - T.lake.x, wz - T.lake.z);
  const lake = smooth(T.lake.r, T.lake.r + 2.5, lakeD);                       // 0 in the lake basin
  const campD = Math.hypot(wx - T.camp.x, wz - T.camp.z);
  const camp = smooth(T.camp.r, T.camp.r + 3, campD);                         // 0 on the flat camp ground
  const mask = clear * road * lake * camp;
  // Low-frequency, broad swells → smooth, gentle ground (no jagged hills).
  const n = Math.sin(wx * 0.16) + Math.sin(wz * 0.14) + 0.5 * Math.sin((wx + wz) * 0.09);
  const hills = Math.max(0, Math.min(1, n * 0.28 + 0.5));
  return hills * T.hillAmp * mask;
}

/** Northern walk boundary (world z) — the deep end past the forks. */
export const kaponNorthBoundZ = T.northBound;

/** A vivid stylized fantasy water texture: a bright teal→deep-blue gradient with layered ripple bands +
 *  caustic sparkle, so the lake reads as lively shimmering water rather than a flat sheet. */
// --- procedural texture toolkit: seamless tileable value noise (sum of integer-frequency sines, so it
// wraps perfectly), used to paint a richer grass ground + caustic water without any external assets. ---
function mulberry32(seed: number): () => number {
  return () => { seed |= 0; seed = (seed + 0x6d2b79f5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
/** A seamless, near-ISOTROPIC fbm noise field on [0,size): per octave we sum several sine waves spread
 *  around all directions (integer frequencies → tiles with no seam), so the result is blobby (not the
 *  diagonal corduroy a couple of plane waves give). ~[-1,1]. */
function tileNoise(seed: number, octaves: number, base: number): (x: number, y: number, size: number) => number {
  const r = mulberry32(seed);
  const terms: { fx: number; fy: number; ph: number; amp: number }[] = [];
  let amp = 1, freq = base, norm = 0;
  for (let o = 0; o < octaves; o++) {
    for (let k = 0; k < 7; k++) { // 7 directions per octave → isotropic blobs
      const ang = r() * Math.PI * 2;
      const fx = Math.round(Math.cos(ang) * freq), fy = Math.round(Math.sin(ang) * freq);
      if (!fx && !fy) continue;
      terms.push({ fx, fy, ph: r() * Math.PI * 2, amp }); norm += amp;
    }
    amp *= 0.55; freq = Math.round(freq * 2);
  }
  const K = (2 * Math.PI);
  return (x, y, size) => {
    let v = 0;
    for (const t of terms) v += t.amp * Math.sin((K / size) * (t.fx * x + t.fy * y) + t.ph);
    return v / norm;
  };
}
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** A vivid, higher-res RPG meadow-grass ground tile: large painterly colour patches + fine blade grain +
 *  subtle vertical blade streaks, over a rich multi-green palette. Seamless, so it tiles cleanly. */
function makeGrassTexture(): THREE.CanvasTexture {
  const s = 384, c = document.createElement("canvas"); c.width = c.height = s;
  const ctx = c.getContext("2d")!;
  const img = ctx.createImageData(s, s), d = img.data;
  const patch = tileNoise(1337, 4, 2);   // big soft colour blobs (painterly variation)
  const flow = tileNoise(4242, 3, 5);    // mid-scale variation
  const grain = tileNoise(9001, 2, 22);  // fine grain
  // shadow → mid → bright → highlight greens (warm, saturated meadow)
  const pal = [[40, 78, 34], [66, 112, 44], [104, 154, 60], [156, 198, 92]];
  for (let y = 0; y < s; y++) for (let x = 0; x < s; x++) {
    let t = (patch(x, y, s) * 0.6 + flow(x, y, s) * 0.4) * 0.5 + 0.5; // [0,1]
    t = Math.max(0, Math.min(1, t + grain(x, y, s) * 0.1));
    const seg = t * 3, i = Math.min(2, seg | 0), f = seg - i, a = pal[i], b = pal[i + 1];
    const o = (y * s + x) * 4;
    d[o] = lerp(a[0], b[0], f); d[o + 1] = lerp(a[1], b[1], f); d[o + 2] = lerp(a[2], b[2], f); d[o + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  // Scattered short VERTICAL blade marks (grass texture) — varied greens, kept clear of the top edge so
  // the tile stays seamless. Subtle (low alpha) so it reads as blades, not lines.
  const rb = mulberry32(2024);
  for (let i = 0; i < 5200; i++) {
    const h = 2 + (rb() * 3 | 0), x = (rb() * s | 0) + 0.5, y = h + (rb() * (s - h) | 0);
    ctx.strokeStyle = rb() < 0.5 ? "rgba(182,216,112,0.28)" : "rgba(38,70,28,0.30)";
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x, y - h); ctx.stroke();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8; // crisp at grazing angles
  return tex;
}

/** A vivid, higher-res caustic water tile: a depth-varied teal→deep-blue body lit by bright moving
 *  caustic cells + sparkle. Seamless; the water mesh scrolls the UV so the caustics drift + shimmer.
 *  Exported: the city pond (villageScene) shares the same water. */
export function makeWaterTexture(): THREE.CanvasTexture {
  const s = 384, c = document.createElement("canvas"); c.width = c.height = s;
  const ctx = c.getContext("2d")!;
  const img = ctx.createImageData(s, s), d = img.data;
  const depth = tileNoise(77, 3, 3);     // body colour variation
  const caust = tileNoise(303, 3, 7);    // caustic cells
  const caust2 = tileNoise(909, 3, 13);  // finer caustics
  const deepC = [18, 64, 116], midC = [40, 158, 196], hiC = [128, 232, 236], hotC = [210, 252, 255]; // deep → teal → bright → glint
  for (let y = 0; y < s; y++) for (let x = 0; x < s; x++) {
    const base = Math.max(0, Math.min(1, depth(x, y, s) * 0.5 + 0.5)); // body depth variation (no y-gradient → seamless)
    let r = lerp(deepC[0], midC[0], base), g = lerp(deepC[1], midC[1], base), b = lerp(deepC[2], midC[2], base);
    const cv = caust(x, y, s) * 0.55 + caust2(x, y, s) * 0.45;
    const bright = Math.min(1, Math.max(0, cv - 0.16) * 2.2);   // prominent caustic webbing
    r = lerp(r, hiC[0], bright); g = lerp(g, hiC[1], bright); b = lerp(b, hiC[2], bright);
    const hot = Math.min(1, Math.max(0, cv - 0.5) * 3);          // sharp white-teal glints
    r = lerp(r, hotC[0], hot); g = lerp(g, hotC[1], hot); b = lerp(b, hotC[2], hot);
    const o = (y * s + x) * 4;
    d[o] = r; d[o + 1] = g; d[o + 2] = b; d[o + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  // a scatter of bright sparkle flecks on top (seamless: positions are within the tile)
  const rr = mulberry32(555); ctx.fillStyle = "#eafdff"; ctx.globalAlpha = 0.6;
  for (let i = 0; i < 140; i++) ctx.fillRect((rr() * s) | 0, (rr() * s) | 0, 2, 2);
  ctx.globalAlpha = 1;
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * Build the Kapon meadow onto `group`: the (mostly flat) grass terrain, the paved→dirt trail, the
 * lake + river, a stone bridge, scattered rocks, dense grass plants, and tree groves. Returns extra
 * blockers (water + rocks + the river boundary) and colliders for the caller to merge.
 */
export function buildKaponTerrain(group: THREE.Group, cx: number, cz: number): {
  blockers: Blocker[]; colliders: THREE.Object3D[]; tick: (dt: number) => void;
} {
  const blockers: Blocker[] = [];
  const colliders: THREE.Object3D[] = [];
  const ticks: ((dt: number) => void)[] = [];
  const gateZ = cz + T.hd; // the south edge / city gate line

  // --- gently-undulating grass terrain mesh (extended past the walkable rect by MARGIN so flat ground
  // runs UNDER the perimeter forest tree-line; kaponHeightAt is 0 out there, so the margin is flat) ---
  const MARGIN = 16;
  const HWm = T.hw + MARGIN, HDm = T.hd + MARGIN;
  const SEG = 112;
  const geo = new THREE.PlaneGeometry(HWm * 2, HDm * 2, SEG, SEG);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const lx = pos.getX(i), ly = pos.getY(i);
    // After rotation.x = −π/2, local (lx,ly) → world (cx+lx, height, cz−ly); displacing local z lifts world y.
    pos.setZ(i, kaponHeightAt(cx + lx, cz - ly));
  }
  geo.computeVertexNormals();
  const grass = makeGrassTexture(); grass.repeat.set(HWm / 2.6, HDm / 2.6); // vivid procedural meadow (painterly patches at a larger scale)
  const terrain = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ map: grass, roughness: 1 }));
  terrain.rotation.x = -Math.PI / 2; terrain.position.set(cx, 0, cz);
  group.add(terrain);

  // --- ROADS: paved lane out of the gate → narrow dirt trunk → it FORKS past the goblin camp into an
  // east branch (to the cave) and a west branch (to the forest). The trunk is axis-aligned; the diagonal
  // forks are stamped from overlapping dirt quads (no rotation maths, reads as a continuous trail). ---
  const dirtTex = tile(`${SCENE}/kapon/path.png`, 1, 1);
  const dirtMat = new THREE.MeshStandardMaterial({ map: dirtTex, roughness: 1 });
  const paved = new THREE.Mesh(new THREE.PlaneGeometry(PATH_W, PAVED_LEN),
    new THREE.MeshStandardMaterial({ map: tile(`${SCENE}/city/pavement.png`, PATH_W / 2, PAVED_LEN / 2), roughness: 1 }));
  paved.rotation.x = -Math.PI / 2; paved.position.set(cx, 0.05, gateZ - PAVED_LEN / 2); group.add(paved);
  // dirt trunk (gate → fork), narrower through the entry corridor.
  const trunkZ0 = gateZ - PAVED_LEN, trunkLen = trunkZ0 - T.forkZ;
  const trunk = new THREE.Mesh(new THREE.PlaneGeometry(PATH_W, trunkLen),
    new THREE.MeshStandardMaterial({ map: tile(`${SCENE}/kapon/path.png`, PATH_W / 2, trunkLen / 3), roughness: 1 }));
  trunk.rotation.x = -Math.PI / 2; trunk.position.set(cx, 0.045, (trunkZ0 + T.forkZ) / 2); group.add(trunk);
  let dotI = 0;
  const stampRoad = (ax: number, az: number, bx: number, bz: number): void => {
    const len = Math.hypot(bx - ax, bz - az), steps = Math.ceil(len / 1.3);
    for (let i = 0; i <= steps; i++) {
      const t = i / steps, x = ax + (bx - ax) * t, z = az + (bz - az) * t;
      const q = new THREE.Mesh(new THREE.PlaneGeometry(PATH_W, PATH_W), dirtMat);
      q.rotation.x = -Math.PI / 2; q.position.set(x, 0.044 + (dotI++ % 4) * 0.0006, z); group.add(q);
    }
  };
  stampRoad(cx, T.forkZ, T.cave.x, T.cave.z);     // east fork → cave
  stampRoad(cx, T.forkZ, T.forest.x, T.forest.z); // west fork → forest

  // --- LAKE (west of the road, open area) — vivid shimmering water. No river crossing any more. ---
  // ORGANIC lake — an irregular caustic-water blob (NOT a circle), no foam ring. The outline radius
  // wobbles smoothly with angle; the same outline drives the visual shape AND the collision.
  const lakeR = (a: number): number => T.lake.r * (0.82 + 0.1 * Math.sin(a * 3 + 1.1) + 0.06 * Math.sin(a * 5 + 2.3) + 0.045 * Math.sin(a * 7 + 4.1));
  const shape = new THREE.Shape();
  for (let i = 0, NP = 64; i <= NP; i++) {
    const a = (i / NP) * Math.PI * 2, rr = lakeR(a);
    const lx = Math.cos(a) * rr, ly = Math.sin(a) * rr;
    i === 0 ? shape.moveTo(lx, ly) : shape.lineTo(lx, ly);
  }
  const lakeTex = makeWaterTexture(); lakeTex.repeat.set(0.16, 0.16); // ShapeGeometry UVs = world units → tile ~6u
  const lakeMat = new THREE.MeshStandardMaterial({ map: lakeTex, transparent: true, opacity: 0.92, roughness: 0.14, metalness: 0.45 });
  const lakeMesh = new THREE.Mesh(new THREE.ShapeGeometry(shape, 10), lakeMat);
  lakeMesh.rotation.x = -Math.PI / 2; lakeMesh.position.set(T.lake.x, 0.07, T.lake.z); group.add(lakeMesh);
  let lakeT = 0;
  ticks.push((dt) => {
    lakeT += dt;
    lakeTex.offset.y = (lakeTex.offset.y + dt * 0.04) % 1; lakeTex.offset.x = Math.sin(lakeT * 0.3) * 0.02;
    lakeMat.opacity = 0.88 + Math.sin(lakeT * 1.3) * 0.05;
  });
  // COLLISION: approximate the blob with a stack of axis-aligned slab boxes (the collision system
  // only supports boxes). Each slab hugs the outline's ACTUAL left/right extent within its z band
  // (the wobbled blob is asymmetric — a symmetric ±max slab overshot the shore), pulled slightly
  // INSIDE the water (INSET) so the fisherman can stand right at the visible edge.
  const LAKE_INSET = 0.35;
  for (let b = 0, NS = 18; b < NS; b++) {
    const z0 = -T.lake.r + 2 * T.lake.r * (b / NS), z1 = -T.lake.r + 2 * T.lake.r * ((b + 1) / NS);
    let xmin = Infinity, xmax = -Infinity;
    for (let i = 0; i < 320; i++) {
      const a = (i / 320) * Math.PI * 2, rr = lakeR(a), px = Math.cos(a) * rr, pz = Math.sin(a) * rr;
      if (pz >= z0 - 0.2 && pz <= z1 + 0.2) { xmin = Math.min(xmin, px); xmax = Math.max(xmax, px); }
    }
    if (xmax - xmin > 2 * LAKE_INSET + 0.3) {
      blockers.push({ x0: (T.lake.x + xmin + LAKE_INSET) * PX, x1: (T.lake.x + xmax - LAKE_INSET) * PX, y0: (T.lake.z + z0) * PX, y1: (T.lake.z + z1) * PX });
    }
  }

  // --- plant/tree billboard helpers + region guards ---
  const overWater = (x: number, z: number): boolean => Math.hypot(x - T.lake.x, z - T.lake.z) < T.lake.r + 1.5;
  const onRoad = (x: number, z: number): boolean => kaponRoadDist(x, z) < PATH_W / 2 + 0.6;
  const inCamp = (x: number, z: number): boolean => Math.hypot(x - T.camp.x, z - T.camp.z) < T.camp.r + 1;
  const free = (x: number, z: number): boolean => !overWater(x, z) && !onRoad(x, z) && !inCamp(x, z);
  const treeTex = [pixTex(`${SCENE}/kapon/tree.png`), pixTex(`${SCENE}/kapon/tree2.png`), pixTex(`${SCENE}/kapon/tree3.png`)];
  const bushTex = pixTex(`${SCENE}/kapon/bush.png`);
  // baseAnchor = fraction of the sprite that is empty padding below the prop's feet (the tree art has
  // ~5-7% transparent space under the trunk → it floats unless we anchor at the actual base, worse the
  // bigger it's scaled).
  const billboard = (tex: THREE.Texture, x: number, z: number, s: number, baseAnchor = 0): void => {
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, alphaTest: 0.4 }));
    sp.center.set(0.5, baseAnchor); sp.scale.set(s, s, 1); sp.position.set(x, kaponHeightAt(x, z) + 0.02, z); group.add(sp);
    distanceBlur.register(sp, tex); // soften with distance
    dayNight.registerMat(sp.material, sp.position); // darken/cool with the day/night cycle (+ nearby lamps)
  };
  const tree = (x: number, z: number, s: number): void => billboard(treeTex[Math.floor(Math.random() * treeTex.length)], x, z, s, 0.055);

  // === ENTRY CORRIDOR: a narrow tree-lined approach hugging both sides of the trunk from the gate to
  // where the meadow opens up (narrowZ). Dense, close-set trees make it read as a tight forest path. ===
  for (let z = gateZ - PAVED_LEN; z >= T.narrowZ - 1; z -= 2.4) {
    for (const side of [-1, 1] as const) {
      tree(cx + side * (T.entryHalf + 1.6 + Math.random() * 0.8), z + (Math.random() - 0.5) * 1.4, 3.8 + Math.random() * 1.4);
      if (Math.random() < 0.5) tree(cx + side * (T.entryHalf + 4.2 + Math.random() * 1.2), z + (Math.random() - 0.5) * 1.6, 4.2 + Math.random() * 1.4);
      if (Math.random() < 0.6) billboard(bushTex, cx + side * (T.entryHalf + 1.0 + Math.random()), z + 0.6, 0.9 + Math.random() * 0.5);
    }
  }

  // === GOBLIN OUTPOST: 4-DIRECTION pixel-art hide TENTS + SKULL-ON-A-STICK totems (the right side shows
  // as the orbit camera moves), around a 3D fire-pit topped with an ANIMATED 2D flame. Goblins hold it. ===
  // A directional billboard: a camera-facing sprite that swaps to one of 4 textures [S,E,N,W] by the orbit
  // yaw (same convention as the character billboards), so a fixed-facing prop shows its correct side.
  // baseAnchor = the fraction of the texture height that is empty padding below the prop's feet, so the
  // visible base sits ON the ground (the rotated tents have ~5% bottom padding → they'd float otherwise).
  const dirSprite = (texs: THREE.Texture[], x: number, z: number, w: number, h: number, facing = 0, baseAnchor = 0): void => {
    const mat = new THREE.SpriteMaterial({ map: texs[0], transparent: true, alphaTest: 0.4 });
    const sp = new THREE.Sprite(mat); sp.center.set(0.5, baseAnchor); sp.scale.set(w, h, 1);
    sp.position.set(x, kaponHeightAt(x, z) + 0.02, z); group.add(sp);
    dayNight.registerMat(mat, sp.position); // (distance-blur swaps the map too, so skip it for directional props)
    let cur = -1;
    ticks.push(() => {
      const rel = ((facing - cameraView.yaw) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
      const idx = Math.round(rel / (Math.PI / 2)) % 4;
      if (idx !== cur) { cur = idx; mat.map = texs[idx]; mat.needsUpdate = true; }
    });
  };
  const tentDirs = ["s", "e", "n", "w"].map((d) => pixTex(`${SCENE}/kapon/tent_${d}.png`));
  const skullDirs = ["s", "e", "n", "w"].map((d) => pixTex(`${SCENE}/kapon/skull_${d}.png`));
  // tents — door faces SOUTH (toward the approaching player); sit ON the ground (baseAnchor skips the
  // ~5% bottom padding); each gets a foot blocker.
  for (const [tx, tz, s] of [[T.camp.x - 10, T.camp.z - 6, 4.0], [T.camp.x + 10, T.camp.z - 5, 4.3], [T.camp.x - 11, T.camp.z + 7, 4.1], [T.camp.x + 9, T.camp.z + 8, 3.8]] as const) {
    dirSprite(tentDirs, tx, tz, s, s, 0, 0.055);
    const hw = s * 0.32;
    blockers.push({ x0: (tx - hw) * PX, x1: (tx + hw) * PX, y0: (tz - hw * 0.55) * PX, y1: (tz + hw * 0.55) * PX });
  }
  // skull-on-a-stick warning totems flanking the camp (square 128×128 art → uniform scale).
  for (const [sx, sz, h] of [[T.camp.x - 6, T.camp.z - 1, 5.0], [T.camp.x + 7, T.camp.z + 2, 4.4]] as const) {
    dirSprite(skullDirs, sx, sz, h, h, 0);
    blockers.push({ x0: (sx - 0.5) * PX, x1: (sx + 0.5) * PX, y0: (sz - 0.5) * PX, y1: (sz + 0.5) * PX });
  }
  // 3D FIRE PIT — a stone ring + crossed logs (real geometry, holds its shape), topped with an animated
  // 2D flame sprite (4 frames cycled) that stays full-bright + casts a flickering warm light.
  const fx = T.camp.x + 4, fz = T.camp.z, fy = kaponHeightAt(fx, fz);
  for (let a = 0; a < 8; a++) { // stone ring
    const st = new THREE.Mesh(new THREE.DodecahedronGeometry(0.32), new THREE.MeshStandardMaterial({ color: a % 2 ? 0x6d6a64 : 0x595650, roughness: 1, flatShading: true }));
    st.position.set(fx + Math.cos(a / 8 * Math.PI * 2) * 1.15, fy + 0.1, fz + Math.sin(a / 8 * Math.PI * 2) * 1.15); st.scale.y = 0.55; group.add(st);
  }
  for (const r of [0.4, -0.4, 1.4] as const) { // crossed/stacked logs
    const log = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.14, 1.9), new THREE.MeshStandardMaterial({ color: 0x4a3826, roughness: 1, flatShading: true }));
    log.rotation.set(Math.PI / 2, 0, r); log.position.set(fx, fy + 0.18, fz); group.add(log);
  }
  const flameTexs = [0, 1, 2, 3].map((i) => pixTex(`${SCENE}/kapon/flame_${i}.png`));
  const flameMat = new THREE.SpriteMaterial({ map: flameTexs[0], transparent: true, alphaTest: 0.35, depthWrite: false });
  const flameSp = new THREE.Sprite(flameMat); flameSp.center.set(0.5, 0);
  const FH = 2.5; flameSp.scale.set(FH * (80 / 58), FH, 1); flameSp.position.set(fx, fy + 0.32, fz); group.add(flameSp); // sits on the logs
  const fire = new THREE.PointLight(0xff8a36, 2.6, 16, 2); fire.position.set(fx, fy + 1.3, fz); group.add(fire);
  blockers.push({ x0: (fx - 1.4) * PX, x1: (fx + 1.4) * PX, y0: (fz - 1.4) * PX, y1: (fz + 1.4) * PX });
  let fireT = 0, frame = -1;
  ticks.push((dt) => {
    fireT += dt;
    const f = 0.85 + Math.sin(fireT * 13) * 0.1 + Math.sin(fireT * 27) * 0.05;
    fire.intensity = 2.6 * f;
    const ni = Math.floor(fireT * 11) % 4; // ~11 fps flame cycle
    if (ni !== frame) { frame = ni; flameMat.map = flameTexs[ni]; flameMat.needsUpdate = true; }
  });

  // === CAVE ENTRANCE (east fork end): a dark arch set into a big mossy rock mound, framed by boulders. ===
  const darkStone = new THREE.MeshStandardMaterial({ color: 0x4b4f4a, roughness: 1, flatShading: true });
  const cy = kaponHeightAt(T.cave.x, T.cave.z);
  const mound = new THREE.Mesh(new THREE.DodecahedronGeometry(6, 0), darkStone);
  mound.scale.set(1.7, 1.05, 1.25); mound.position.set(T.cave.x, cy + 1.6, T.cave.z - 1.5); mound.rotation.y = 0.5; group.add(mound); colliders.push(mound);
  const mouth = new THREE.Mesh(new THREE.CircleGeometry(2.0, 16), new THREE.MeshBasicMaterial({ color: 0x05060a }));
  mouth.scale.y = 1.3; mouth.position.set(T.cave.x, cy + 2.0, T.cave.z + 1.2); group.add(mouth); // faces south (the approach)
  for (const ox of [-3.4, 3.6] as const) { // framing boulders
    const b = new THREE.Mesh(new THREE.DodecahedronGeometry(1.7), darkStone);
    b.scale.set(1.2, 0.9, 1.1); b.position.set(T.cave.x + ox, cy + 0.7, T.cave.z + 1.6); b.rotation.set(ox, 1, 0.4); group.add(b); colliders.push(b);
  }
  blockers.push({ x0: (T.cave.x - 9) * PX, x1: (T.cave.x + 9) * PX, y0: (T.cave.z - 7) * PX, y1: (T.cave.z + 0.4) * PX });

  // === FOREST PATH (west fork end): the dirt fork fades into a DENSE stand of trees + bushes. ===
  for (let i = 0; i < 40; i++) {
    const ang = Math.random() * Math.PI * 2, rad = 2 + Math.random() * 13;
    const x = T.forest.x + Math.cos(ang) * rad * 1.3, z = T.forest.z + Math.sin(ang) * rad - 2;
    if (onRoad(x, z)) continue;
    if (Math.random() < 0.8) tree(x, z, 4.0 + Math.random() * 1.8); else billboard(bushTex, x, z, 1.0 + Math.random() * 0.7);
  }

  // === scattered mossy boulders + open-area groves + bushes (all kept clear of road/water/camp) ===
  for (const [rx, rz, r] of [[cx - 24, cz - 22, 1.3], [cx + 30, cz - 18, 1.5], [cx + 26, cz + 16, 1.3], [cx - 28, cz + 18, 1.1], [cx + 16, cz - 30, 0.9]] as const) {
    if (!free(rx, rz)) continue;
    const y = kaponHeightAt(rx, rz);
    const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(r), new THREE.MeshStandardMaterial({ color: 0x747871, roughness: 1, flatShading: true }));
    rock.scale.set(1.35, 0.6, 1.15); rock.position.set(rx, y + r * 0.28, rz); rock.rotation.set(rx, rz, 0.3);
    group.add(rock); colliders.push(rock);
    blockers.push({ x0: (rx - r) * PX, x1: (rx + r) * PX, y0: (rz - r) * PX, y1: (rz + r) * PX });
  }
  // MANY more groves through the meadow (each a clump of 3-5 trees + bushes) — fuller, layered woods.
  const groves: ReadonlyArray<readonly [number, number]> = [
    [cx - 30, cz + 24], [cx + 32, cz + 20], [cx - 34, cz - 8], [cx + 34, cz - 6], [cx - 18, cz + 30], [cx + 20, cz + 30],
    [cx - 42, cz + 4], [cx + 42, cz + 8], [cx - 26, cz - 26], [cx + 28, cz - 28], [cx - 46, cz - 12], [cx + 46, cz - 10],
    [cx - 38, cz + 32], [cx + 40, cz + 30], [cx - 14, cz + 34], [cx + 16, cz + 34], [cx - 50, cz + 18], [cx + 50, cz + 16],
  ];
  for (const [gx, gz] of groves) {
    if (!free(gx, gz)) continue;
    const n = 3 + (Math.random() * 3 | 0);
    for (let i = 0; i < n; i++) tree(gx + (Math.random() - 0.5) * 5.2, gz + (Math.random() - 0.5) * 4.2, 3.2 + Math.random() * 2.0);
    billboard(bushTex, gx + (Math.random() < 0.5 ? -2.4 : 2.4), gz + 2.4, 1.2 + Math.random() * 0.7);
  }
  // scattered solo trees + saplings sprinkled across the meadow (off road/water/camp) for visual depth.
  for (let i = 0; i < 70; i++) {
    const x = cx - T.hw + 5 + Math.random() * (T.hw * 2 - 10);
    const z = kaponNorthBoundZ + 4 + Math.random() * (gateZ - 6 - (kaponNorthBoundZ + 4));
    if (!free(x, z)) continue;
    if (Math.random() < 0.45) tree(x, z, 2.6 + Math.random() * 2.2); else billboard(bushTex, x, z, 0.85 + Math.random() * 0.8);
  }

  // === DISTANT MOUNTAINS: a range BUILT FROM INDIVIDUAL peaks (3 variants) — many standalone mountains
  // placed at different DEPTHS (x), positions (z), sizes + haze tints, so near peaks overlap + occlude far
  // ones into a real layered range (no cut-off "panorama art board"). FLAT panels (don't rotate); the
  // tree-line in front hides their bases, so they read as mountains rising behind the woods. ===
  // The range is mostly LOW SNOW mountains (short, wide, snow-capped), with FEWER tall FORESTED/ROCKY
  // peaks poking up as accents. Each variant keeps its own aspect so it isn't stretched.
  const lowSnow = [pixTex(`${SCENE}/kapon/mtn_snow1.png`), pixTex(`${SCENE}/kapon/mtn_snow2.png`)];
  const highMtn = [pixTex(`${SCENE}/kapon/mtn_forest.png`), pixTex(`${SCENE}/kapon/mtn_rock.png`)];
  const SNOW_AS = 152 / 104, HIGH_AS = 120 / 160;
  const mountain = (texs: THREE.Texture[], aspect: number, x: number, z: number, w: number, faceEast: boolean, tint: number, op: number): void => {
    const tex = texs[(Math.random() * texs.length) | 0], h = w / aspect;
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ map: tex, transparent: true, alphaTest: 0.3, depthWrite: false, color: tint, opacity: op }));
    m.position.set(x, h / 2 - 2, z); m.rotation.y = faceEast ? Math.PI / 2 : -Math.PI / 2; group.add(m); // base ~2u below ground → hidden by the tree-line
  };
  const span = T.hd * 2 + 36;
  // WEST: 3 depth rows of LOW SNOW mountains (far small+hazy behind → near bigger front), overlapping.
  for (const r of [
    { x: cx - T.hw - 58, wmin: 26, wmax: 34, tint: 0xe9f1f8, op: 0.9, n: 7 },
    { x: cx - T.hw - 42, wmin: 32, wmax: 42, tint: 0xdde6ef, op: 0.95, n: 6 },
    { x: cx - T.hw - 26, wmin: 40, wmax: 52, tint: 0xd4dfe9, op: 1.0, n: 5 },
  ]) {
    for (let i = 0; i < r.n; i++) {
      const z = cz - span / 2 + span * ((i + Math.random() * 0.7) / r.n);
      mountain(lowSnow, SNOW_AS, r.x + (Math.random() - 0.5) * 9, z, r.wmin + Math.random() * (r.wmax - r.wmin), true, r.tint, r.op);
    }
  }
  // WEST: a FEW tall FORESTED/ROCKY peaks rising above the snowy range (accents — fewer + taller).
  for (let i = 0; i < 4; i++) {
    const z = cz - span / 2 + span * ((i + 0.4 + Math.random() * 0.5) / 4);
    mountain(highMtn, HIGH_AS, cx - T.hw - (34 + Math.random() * 14), z, 22 + Math.random() * 8, true, 0xd6e0ea, 0.97);
  }
  // EAST: a couple of low snow + one tall forested peak as the far "end of the woods".
  for (let i = 0; i < 4; i++) mountain(lowSnow, SNOW_AS, cx + T.hw + 30 + (Math.random() - 0.5) * 8, cz - span / 2 + span * ((i + Math.random() * 0.6) / 4), 32 + Math.random() * 10, false, 0xd2dde8, 0.96);
  mountain(highMtn, HIGH_AS, cx + T.hw + 38, cz + (Math.random() - 0.5) * 20, 24, false, 0xd2dde8, 0.96);

  // === PERIMETER FOREST: an IRREGULAR, DEEP tree-line outside the walkable W / E / N edges — the depth
  // WEAVES in and out (organic, not a straight wall), 4 rows + a hazy far-back row that fades into fog,
  // so the woods read as dense + receding. South = the city gate (no forest). ===
  const jit = (): number => (Math.random() - 0.5) * 1.6;
  const forest = (x: number, z: number, s = 4.4 + Math.random() * 1.8): void => tree(x + jit(), z + jit(), s);
  // bluish, hazed FAR trees for atmospheric depth behind the near rows.
  const farTree = (x: number, z: number, s: number): void => {
    const t = treeTex[Math.floor(Math.random() * treeTex.length)];
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: t, transparent: true, alphaTest: 0.4, color: 0xa6bbc9, opacity: 0.9 }));
    sp.center.set(0.5, 0.055); sp.scale.set(s, s, 1); sp.position.set(x, kaponHeightAt(x, z) + 0.02, z); group.add(sp);
    distanceBlur.register(sp, t);
  };
  for (let z = kaponNorthBoundZ - 2; z <= gateZ + 6; z += 2.2) {
    const wob = Math.sin(z * 0.17) * 3.2 + (Math.random() - 0.5) * 2.2; // weave the tree-line in/out
    for (let row = 0; row < 4; row++) {
      const dep = 1.5 + row * 3 + wob * (row < 2 ? 1 : 0.35);
      forest(cx - T.hw - dep, z); forest(cx + T.hw + dep, z);
    }
    farTree(cx - T.hw - 15 + wob, z + jit(), 5.4 + Math.random() * 2.2);
    farTree(cx + T.hw + 15 - wob, z + jit(), 5.4 + Math.random() * 2.2);
  }
  for (let x = cx - T.hw - 6; x <= cx + T.hw + 6; x += 2.3) {          // north tree-line (deep end)
    const nz = kaponNorthBoundZ - 3, wob = Math.sin(x * 0.16) * 2.6;
    for (let row = 0; row < 4; row++) forest(x, nz - row * 3 - wob * (row < 2 ? 1 : 0.35));
    farTree(x + jit(), nz - 15 - wob, 5.4 + Math.random() * 2.2);
  }

  // === ORGANIC THICKETS: dense tree+bush clumps that jut INTO the meadow at irregular spots, each a soft
  // blocker, so the navigable space WINDS around them instead of reading as a rigid rectangle. ===
  for (const [tx, tz] of [[cx - T.hw + 13, cz - 30], [cx + T.hw - 15, cz - 6], [cx - T.hw + 19, cz + 28], [cx + T.hw - 21, cz + 32], [cx - 16, cz - 16], [cx + 24, cz + 6], [cx - T.hw + 24, cz - 2]] as const) {
    if (!free(tx, tz)) continue;
    for (let i = 0; i < 4; i++) tree(tx + (Math.random() - 0.5) * 5, tz + (Math.random() - 0.5) * 5, 3.4 + Math.random() * 1.8);
    billboard(bushTex, tx, tz + 1.6, 1.4);
    blockers.push({ x0: (tx - 2.2) * PX, x1: (tx + 2.2) * PX, y0: (tz - 2.2) * PX, y1: (tz + 2.2) * PX });
  }

  // --- DENSE grass plants blanketing the meadow (same cuttable-grass tuft model as the city, base-
  // anchored + swaying in the wind). Denser scatter than before for a lusher field. ---
  const tufts: { mat: THREE.SpriteMaterial; phase: number; speed: number; amp: number }[] = [];
  const STEP = 2.5; // tighter grid → a lot more grass
  for (let gx = cx - T.hw + 2; gx <= cx + T.hw - 2; gx += STEP) {
    for (let gz = kaponNorthBoundZ + 1.5; gz <= gateZ - 1.5; gz += STEP) {
      const x = gx + (Math.random() - 0.5) * STEP * 0.9, z = gz + (Math.random() - 0.5) * STEP * 0.9;
      if (!free(x, z)) continue;
      const tuftTex = grassTuftTexture((Math.random() * GRASS_TUFT_VARIANTS) | 0);
      const mat = new THREE.SpriteMaterial({ map: tuftTex, transparent: true, alphaTest: 0.4 });
      const sp = new THREE.Sprite(mat);
      sp.center.set(0.5, 0); // base-anchored: sways around its root
      const s = 0.5 + Math.random() * 0.5; sp.scale.set(s, s, 1);
      sp.position.set(x, kaponHeightAt(x, z) + 0.02, z); group.add(sp);
      distanceBlur.register(sp, tuftTex); // soften with distance
      dayNight.registerMat(mat, sp.position); // darken/cool with the day/night cycle (+ nearby lamps)
      tufts.push({ mat, phase: Math.random() * Math.PI * 2, speed: 0.9 + Math.random() * 0.7, amp: 0.07 + Math.random() * 0.07 });
    }
  }
  let windT = 0;
  ticks.push((dt) => { windT += dt; for (const t of tufts) t.mat.rotation = Math.sin(windT * t.speed + t.phase) * t.amp; });

  return { blockers, colliders, tick: (dt) => ticks.forEach((f) => f(dt)) };
}
